import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { api } from '../../lib/api'

interface Tariff { id: string; name: string; bikeType: string; hourlyRate: string; dailyRate?: string; crossPointFee: string; isActive: boolean; color?: string | null }

type EditTariffForm = { name: string; hourlyRate: number; dailyRate: number; crossPointFee: number; color: string }

const TARIFF_COLOR_PRESETS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#64748b',
]

/** Цветная точка для тарифа — используется в любом месте интерфейса */
export function TariffDot({ color, size = 10 }: { color?: string | null; size?: number }) {
  if (!color) return null
  return (
    <span
      style={{ width: size, height: size, backgroundColor: color, display: 'inline-block', borderRadius: '50%', flexShrink: 0 }}
    />
  )
}

const PRESET_BIKE_TYPES = [
  { value: 'adult', label: 'Взрослый' },
  { value: 'child', label: 'Детский' },
  { value: 'electric', label: 'Электро' },
  { value: 'lock', label: 'Велозамок' },
  { value: 'childSeat', label: 'Детское кресло' },
  { value: 'cart', label: 'Корзина/тележка' },
]

export default function TariffsPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [customType, setCustomType] = useState(false)
  const [editTariff, setEditTariff] = useState<Tariff | null>(null)
  const { data } = useQuery({ queryKey: ['tariffs'], queryFn: () => api.get('/tariffs').then((r) => r.data) })
  const { register, handleSubmit, reset, setValue, watch } = useForm<{ name: string; bikeType: string; hourlyRate: number; dailyRate: number; crossPointFee: number; color: string }>()
  const editForm = useForm<EditTariffForm>()
  const bikeTypeValue = watch('bikeType')

  const [createError, setCreateError] = useState<string | null>(null)

  // A tariff price change must also drop caches that embed bike.tariff (bikes list,
  // scanned bikes), otherwise the new-rental estimate keeps the stale price.
  const invalidateTariffDependent = () => {
    qc.invalidateQueries({ queryKey: ['tariffs'] })
    qc.invalidateQueries({ queryKey: ['bikes'] })          // matches ['bikes'] and ['bikes', pointId]
    qc.invalidateQueries({ queryKey: ['bikes-at-point'] })
  }

  const create = useMutation({
    mutationFn: (d: object) => api.post('/tariffs', d),
    onSuccess: () => { invalidateTariffDependent(); reset(); setShowForm(false); setCreateError(null); setCustomType(false) },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } }
      setCreateError(e?.response?.data?.error?.message ?? e?.response?.data?.message ?? 'Ошибка при сохранении')
    },
  })

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) => api.patch(`/tariffs/${id}`, data),
    onSuccess: () => { invalidateTariffDependent(); setEditTariff(null) },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } }
      alert(e?.response?.data?.error?.message ?? 'Ошибка при сохранении')
    },
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/tariffs/${id}`, { isActive }),
    onSuccess: () => invalidateTariffDependent(),
  })

  const openEdit = (t: Tariff) => {
    editForm.reset({
      name: t.name,
      hourlyRate: Number(t.hourlyRate),
      dailyRate: Number(t.dailyRate ?? 0),
      crossPointFee: Number(t.crossPointFee),
      color: t.color ?? '',
    })
    setEditTariff(t)
  }

  const editColor = editForm.watch('color')

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Тарифы</h2>
        <button onClick={() => setShowForm(!showForm)} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm font-medium">
          + Добавить
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          {createError && <p className="mb-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{createError}</p>}
          <form onSubmit={handleSubmit((d) => create.mutate(d))} className="grid grid-cols-2 gap-3">
            <input {...register('name', { required: true })} placeholder="Название тарифа" className="col-span-2 rounded-xl border-gray-300 text-sm" />
            <div className="space-y-1">
              {!customType ? (
                <select
                  {...register('bikeType', { required: true })}
                  className="w-full rounded-xl border-gray-300 text-sm"
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setCustomType(true)
                      setValue('bikeType', '')
                    } else {
                      setValue('bikeType', e.target.value)
                    }
                  }}
                  value={bikeTypeValue ?? ''}
                >
                  <option value="">Тип инвентаря</option>
                  {PRESET_BIKE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                  <option value="__custom__">+ Свой тип...</option>
                </select>
              ) : (
                <div className="flex gap-1">
                  <input
                    {...register('bikeType', { required: true })}
                    placeholder="Например: самокат"
                    className="flex-1 rounded-xl border-gray-300 text-sm"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => { setCustomType(false); setValue('bikeType', '') }}
                    className="text-xs text-gray-400 px-2"
                  >✕</button>
                </div>
              )}
            </div>
            <input {...register('hourlyRate', { valueAsNumber: true, required: true })} type="number" placeholder="Цена в час (₽)" className="rounded-xl border-gray-300 text-sm" />
            <input {...register('dailyRate', { valueAsNumber: true })} type="number" placeholder="Цена в сутки (₽)" className="rounded-xl border-gray-300 text-sm" />
            <input {...register('crossPointFee', { valueAsNumber: true })} type="number" defaultValue={0} placeholder="Межточечная доплата (₽)" className="rounded-xl border-gray-300 text-sm" />
            <div className="col-span-2">
              <p className="text-xs text-gray-500 mb-1">Цвет маркировки (необязательно):</p>
              <div className="flex gap-2 flex-wrap">
                {TARIFF_COLOR_PRESETS.map((c) => (
                  <button key={c} type="button" onClick={() => setValue('color', c)}
                    style={{ backgroundColor: c, width: 28, height: 28, borderRadius: '50%', border: watch('color') === c ? '3px solid #1e293b' : '2px solid transparent' }}
                  />
                ))}
                <input {...register('color')} type="color" className="w-8 h-8 rounded-lg border-0 cursor-pointer" title="Выбрать цвет" />
              </div>
            </div>
            <div className="col-span-2 flex gap-2">
              <button type="submit" className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm">Сохранить</button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-4 py-2 rounded-xl text-sm">Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {data?.tariffs?.map((t: Tariff) => (
          <div key={t.id} className={`bg-white border rounded-2xl p-4 ${t.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'}`} style={t.color ? { borderLeftColor: t.color, borderLeftWidth: 4 } : {}}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <TariffDot color={t.color} size={12} />
                  {t.name}
                </h3>
                <p className="text-sm text-gray-500">{PRESET_BIKE_TYPES.find((p) => p.value === t.bikeType)?.label ?? t.bikeType}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>В час: <b>{t.hourlyRate} ₽</b></span>
                  {t.dailyRate && <span>В сутки: <b>{t.dailyRate} ₽</b></span>}
                  <span>Межточечная: <b>{t.crossPointFee} ₽</b></span>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${t.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {t.isActive ? 'Активен' : 'Неактивен'}
              </span>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => openEdit(t)} className="text-xs border border-brand-300 text-brand-600 px-3 py-1 rounded-lg hover:bg-brand-50">
                Изменить
              </button>
              <button
                onClick={() => toggleActive.mutate({ id: t.id, isActive: !t.isActive })}
                disabled={toggleActive.isPending}
                className="text-xs border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50"
              >
                {t.isActive ? 'Деактивировать' : 'Активировать'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit tariff modal */}
      {editTariff && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setEditTariff(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base">Редактировать тариф</h3>
              <button onClick={() => setEditTariff(null)} className="text-gray-400 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={editForm.handleSubmit((d) => update.mutate({ id: editTariff.id, data: { ...d, color: d.color || null } }))} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название</label>
                <input {...editForm.register('name', { required: true })} className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Цена в час (₽)</label>
                  <input {...editForm.register('hourlyRate', { valueAsNumber: true, required: true })} type="number" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Цена в сутки (₽)</label>
                  <input {...editForm.register('dailyRate', { valueAsNumber: true })} type="number" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Межточечная доплата (₽)</label>
                <input {...editForm.register('crossPointFee', { valueAsNumber: true })} type="number" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Цвет маркировки</label>
                <div className="flex gap-2 flex-wrap items-center">
                  {TARIFF_COLOR_PRESETS.map((c) => (
                    <button key={c} type="button" onClick={() => editForm.setValue('color', c)}
                      style={{ backgroundColor: c, width: 28, height: 28, borderRadius: '50%', border: editColor === c ? '3px solid #1e293b' : '2px solid transparent', flexShrink: 0 }}
                    />
                  ))}
                  <input {...editForm.register('color')} type="color" className="w-8 h-8 rounded-lg border-0 cursor-pointer" />
                  {editColor && (
                    <button type="button" onClick={() => editForm.setValue('color', '')} className="text-xs text-gray-400 underline">убрать цвет</button>
                  )}
                </div>
              </div>
              <button type="submit" disabled={update.isPending} className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                {update.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
